import type { ReactNode } from "react";
import { get } from "./api.js";
import {
  dayKey,
  dayNumber,
  formatDuration,
  formatWeekdayDay,
} from "./format.js";
import { useAsync } from "./useAsync.js";

export interface PracticeHistoryDay {
  date: string;
  sessions: number;
  totalSeconds: number;
}

export interface PracticeHistorySummary {
  timezone: "Europe/London";
  currentStreakDays: number;
  practisedDaysLast7: number;
  practisedDaysLast30: number;
  totalSecondsLast7: number;
  totalSecondsLast30: number;
  days: PracticeHistoryDay[];
}

function plural(value: number, singular: string): string {
  return `${value} ${singular}${value === 1 ? "" : "s"}`;
}

function wholeNonNegative(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isPracticeHistoryDay(value: unknown): value is PracticeHistoryDay {
  if (!value || typeof value !== "object") return false;
  const day = value as Partial<PracticeHistoryDay>;
  return (
    typeof day.date === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(day.date) &&
    wholeNonNegative(day.sessions) &&
    wholeNonNegative(day.totalSeconds)
  );
}

export function isPracticeHistorySummary(
  value: unknown,
): value is PracticeHistorySummary {
  if (!value || typeof value !== "object") return false;
  const summary = value as Partial<PracticeHistorySummary>;
  return (
    summary.timezone === "Europe/London" &&
    wholeNonNegative(summary.currentStreakDays) &&
    wholeNonNegative(summary.practisedDaysLast7) &&
    wholeNonNegative(summary.practisedDaysLast30) &&
    wholeNonNegative(summary.totalSecondsLast7) &&
    wholeNonNegative(summary.totalSecondsLast30) &&
    Array.isArray(summary.days) &&
    summary.days.every(isPracticeHistoryDay)
  );
}

async function loadPracticeHistory(
  endpoint: string,
): Promise<PracticeHistorySummary> {
  const response = await get<unknown>(endpoint);
  if (!isPracticeHistorySummary(response)) {
    throw new Error("Practice history response was incomplete.");
  }
  return response;
}

/** The spoken equivalent of formatDuration, including its visible seconds precision. */
function spokenDuration(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  if (whole === 0) return "no time";
  if (whole < 60) return plural(whole, "second");

  if (whole < 3600) {
    const minutes = Math.floor(whole / 60);
    const rest = whole % 60;
    return rest === 0
      ? plural(minutes, "minute")
      : `${plural(minutes, "minute")} ${plural(rest, "second")}`;
  }

  const hours = Math.floor(whole / 3600);
  const minutes = Math.round((whole % 3600) / 60);
  return `${plural(hours, "hour")} ${plural(minutes, "minute")}`;
}

export function practiceDayLabel(day: PracticeHistoryDay): string {
  const date = formatWeekdayDay(day.date);
  if (day.sessions === 0) return `${date}: no practice logged.`;
  return `${date}: ${plural(day.sessions, "session")}, ${spokenDuration(day.totalSeconds)}.`;
}

function calendarWeeks(
  days: readonly PracticeHistoryDay[],
): PracticeHistoryDay[][] {
  const weeks: PracticeHistoryDay[][] = [];
  for (let index = 0; index < days.length; index += 7) {
    weeks.push(days.slice(index, index + 7));
  }
  return weeks;
}

export function PracticeHistory({
  endpoint,
  variant,
  showCalendar = false,
  revision = "",
  selectedDate = null,
  onSelectDay,
  collapsible = false,
  children,
}: {
  endpoint: string;
  variant: "family" | "staff";
  showCalendar?: boolean;
  revision?: string | number;
  selectedDate?: string | null;
  onSelectDay?: (day: PracticeHistoryDay) => void;
  collapsible?: boolean;
  children?: ReactNode;
}) {
  const {
    data: loaded,
    error,
    loading,
  } = useAsync(
    async () => ({ endpoint, summary: await loadPracticeHistory(endpoint) }),
    [endpoint, revision],
  );
  // Keep a previous good summary during same-student refreshes, but never show
  // one student's stale data after the route has moved to another student.
  const data = loaded?.endpoint === endpoint ? loaded.summary : null;

  function wrap(content: ReactNode) {
    if (!collapsible) return <>{content}{children}</>;
    return <details className="practice-history-disclosure">
      <summary>
        <strong>Practice history</strong>
        <span className="small muted">{data
          ? `${plural(data.practisedDaysLast7, "practice day")} · ${formatDuration(data.totalSecondsLast7)} in the last 7 days`
          : "View calendar and sessions"}</span>
      </summary>
      {content}
      {children}
    </details>;
  }
  if (!data) {
    return wrap(loading
      ? <section className="panel" aria-label="Practice history"><p className="empty">Loading practice history…</p></section>
      : error
        ? <section className="panel" aria-label="Practice history"><div className="error small">Practice history could not be loaded: {error}</div></section>
        : null);
  }

  const today = dayKey(new Date().toISOString());
  const weeks = calendarWeeks(data.days);

  const content = (
    <section
      className="panel"
      aria-labelledby={`${variant}-practice-summary-heading`}
    >
      <div className="spread" style={{ alignItems: "start", gap: 16 }}>
        <div>
          <h3
            id={`${variant}-practice-summary-heading`}
            style={{ marginTop: 0 }}
          >
            {variant === "family" ? "Last 7 days" : "Practice summary"}
          </h3>
          <p className="lede" style={{ margin: "4px 0" }}>
            {plural(data.practisedDaysLast7, "practice day")} ·{" "}
            {formatDuration(data.totalSecondsLast7)}
          </p>
          <div className="small muted">
            Current streak:{" "}
            <strong>{plural(data.currentStreakDays, "day")}</strong>
          </div>
        </div>
        <div className="small muted" style={{ textAlign: "right" }}>
          <strong>Last 30 days</strong>
          <div>
            {plural(data.practisedDaysLast30, "practice day")} ·{" "}
            {formatDuration(data.totalSecondsLast30)}
          </div>
        </div>
      </div>

      {loading && (
        <p
          className="small muted"
          aria-live="polite"
          style={{ margin: "10px 0 0" }}
        >
          Refreshing practice history…
        </p>
      )}
      {error && (
        <div className="error small" style={{ marginTop: 10 }}>
          Practice history could not be refreshed; showing the previous data.
        </div>
      )}

      {showCalendar && (
        <div className="practice-calendar" style={{ marginTop: 18 }}>
          <div className="spread" style={{ marginBottom: 8 }}>
            <strong>Recent practice</strong>
            <span className="small muted">8 weeks · Europe/London</span>
          </div>
          <div
            className="practice-calendar-scroll"
            role="region"
            aria-label="Recent practice calendar"
            tabIndex={0}
          >
            <div className="practice-calendar-grid">
              <div aria-hidden="true" className="practice-calendar-week">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(
                  (weekday) => (
                    <div
                      className="small muted"
                      style={{ textAlign: "center" }}
                      key={weekday}
                    >
                      {weekday}
                    </div>
                  ),
                )}
              </div>
              {weeks.map((week, weekIndex) => (
                <div
                  key={`week-${weekIndex}`}
                  className="practice-calendar-week"
                >
                  {week.map((day) => {
                    const future = day.date > today;
                    const selected = selectedDate === day.date;
                    return (
                      <div className="practice-calendar-day" key={day.date}>
                        {future ? (
                          <span
                            className="small muted"
                            aria-hidden="true"
                            title={`${formatWeekdayDay(day.date)}: future date`}
                            style={{
                              minHeight: 44,
                              display: "grid",
                              placeItems: "center",
                              opacity: 0.45,
                            }}
                          >
                            {dayNumber(day.date)}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className={selected ? "small" : "quiet small"}
                            aria-label={practiceDayLabel(day)}
                            aria-pressed={selected}
                            onClick={() => onSelectDay?.(day)}
                            style={{
                              width: 44,
                              minHeight: 44,
                              padding: "4px 2px",
                              display: "grid",
                              justifyItems: "center",
                              alignContent: "center",
                              gap: 1,
                            }}
                          >
                            <span aria-hidden="true">
                              {dayNumber(day.date)}
                            </span>
                            <span
                              className="muted tabular"
                              aria-hidden="true"
                              style={{ fontSize: 10, lineHeight: 1.1 }}
                            >
                              {day.sessions > 0
                                ? `${day.sessions}× · ${formatDuration(day.totalSeconds)}`
                                : "—"}
                            </span>
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <p className="small muted" style={{ marginBottom: 0, marginTop: 14 }}>
        Practice time records when sessions were logged; it does not measure the
        quality of the work.
      </p>
    </section>
  );
  return wrap(content);
}
